import React from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../../ui/Button/Button';
import { EmptyNote } from '../ui/EmptyNote';
import { Sheet } from '../ui/Sheet';
import { LOAD_ICON_MAP } from '../../../lib/rcu/catalog/icon';
import { LOAD_TYPE_SPECS } from '../../../lib/rcu/catalog/loadType';
import type { Load, WiredLoad } from '../../../lib/rcu/model';
import type { ChannelTarget } from '../../../lib/rcu/query/targets';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useCommandFeedback } from '../useCommandFeedback';

export interface TargetSheetProps {
  isOpen: boolean;
  /** The output the technician tapped, or null when the sheet should stay closed. */
  target: ChannelTarget | null;
  onClose: () => void;
  /** Opens the load editor for the wired load, or for a new one on this output. */
  onEditLoad: (load: WiredLoad | null, target: ChannelTarget) => void;
}

const CandidateRow: React.FC<{ load: Load; onWire: () => void }> = ({ load, onWire }) => {
  const Icon = LOAD_ICON_MAP[load.iconKey];
  return (
    <button
      type="button"
      onClick={onWire}
      className="flex w-full items-center gap-3 rounded-2xl border border-[#E0E0E0] bg-white px-4 py-3 text-left transition-colors hover:border-[#D4A054]"
    >
      <Icon className="h-5 w-5 shrink-0 text-[#D4A054]" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-[#333333]">{load.name}</span>
        <span className="block text-xs text-[#666666]">{LOAD_TYPE_SPECS[load.type].label}</span>
      </span>
      <Plus className="h-4 w-4 shrink-0 text-[#D4A054]" />
    </button>
  );
};

/**
 * What sits on one output. A free output offers the unwired loads that fit; an
 * occupied one offers edit and unwire.
 */
export const TargetSheet: React.FC<TargetSheetProps> = ({
  isOpen,
  target,
  onClose,
  onEditLoad,
}) => {
  const config = useRcuConfigStore((state) => state.config);
  const bindLoad = useRcuConfigStore((state) => state.bindLoad);
  const unbindLoad = useRcuConfigStore((state) => state.unbindLoad);
  const report = useCommandFeedback();

  if (target === null) return null;

  const wired = target.load;
  const WiredIcon = wired === undefined ? null : LOAD_ICON_MAP[wired.iconKey];
  const candidates = config.loads.filter(
    (load) => load.channel === null && target.acceptedLoadTypes.includes(load.type),
  );

  return (
    <Sheet
      isOpen={isOpen}
      title={target.shortLabel}
      subtitle={wired === undefined ? `Free · ${target.outputHint}` : LOAD_TYPE_SPECS[wired.type].label}
      onClose={onClose}
      footer={
        wired === undefined ? (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                if (report(unbindLoad(wired.id), `${wired.name} unwired.`)) onClose();
              }}
            >
              Unwire
            </Button>
            <Button variant="primary" onClick={() => onEditLoad(wired, target)}>
              Edit load
            </Button>
          </>
        )
      }
    >
      {wired === undefined || WiredIcon === null ? (
        <div className="space-y-4">
          <Button variant="primary" onClick={() => onEditLoad(null, target)} className="w-full">
            New load on this output
          </Button>

          <div>
            <p className="mb-2 text-sm font-medium text-[#333333]">Or wire a load that is waiting</p>
            {candidates.length === 0 ? (
              <EmptyNote message={`No unwired load matches this output (${target.outputHint}).`} />
            ) : (
              <ul className="space-y-2">
                {candidates.map((load) => (
                  <li key={load.id}>
                    <CandidateRow
                      load={load}
                      onWire={() => {
                        if (report(bindLoad(load.id, target.ref), `${load.name} wired.`)) onClose();
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-2xl border border-[#E0E0E0] px-4 py-3">
            <WiredIcon className="h-5 w-5 shrink-0 text-[#D4A054]" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-[#333333]">{wired.name}</p>
              <p className="truncate text-xs text-[#666666]">{target.label}</p>
            </div>
          </div>
          <p className="text-xs text-[#666666]">
            Unwiring keeps the load and its room assignment; it simply stops being controllable
            until it is wired again.
          </p>
        </div>
      )}
    </Sheet>
  );
};

export default TargetSheet;
